/**
 * The ledger's wire shapes — `Booking`, `Account`, `BookingSummaryRow` from `docs/api/paths/{bookings,accounts}.yaml`
 * — and the row types the tables render them as. Pure: safe to import from client components.
 */

import { ExpenseType } from '@/lib/api/expenses/types'
import { IncomeType } from '@/lib/api/incomes/types'
import type { ProjectStatusTypeValue, ProjectTypeValue } from '@/lib/api/projects/types'
import { SalaryType } from '@/lib/api/salaries/types'
import { TransferType } from '@/lib/api/transfers/types'
import { Page, strEnum } from '@/lib/api/types'
import { TableFilters, urlEnum, urlIds } from '@/lib/table-state'

export const AccountType = strEnum(['main', 'expense', 'salary', 'deposit', 'cashout'])
export type AccountTypeValue = keyof typeof AccountType

/** `BookingType`: the union of the transfer, salary, expense and income types, in that order. */
export const BookingType = { ...TransferType, ...SalaryType, ...ExpenseType, ...IncomeType }
export type BookingTypeValue = keyof typeof BookingType

/** The member behind an account — the basic profile, never the whole user. */
export type ApiUserOwner = {
  kind: 'user'
  id: number
  firstname: string
  lastname: string
  email: string
  picture: string | null
  exited: string | null
}

/** The project behind an account. */
export type ApiProjectOwner = {
  kind: 'project'
  id: number
  name: string
  projectType: ProjectTypeValue
  status: ProjectStatusTypeValue
  /** The project's stable key; `company_revenue` is the company itself. */
  slug: string
}

export type ApiAccountOwner = ApiUserOwner | ApiProjectOwner

export type ApiAccount = {
  id: number
  name: string
  type: AccountTypeValue
  /** `Money`: a fixed two-decimal string; `null` for an aspirant on an account neither theirs nor a project's. */
  balance: string | null
  owner: ApiAccountOwner | null
  created: string
  updated: string
}

/** The source entry a booking was derived from — at most one is set (`ReferenceBadge` links it). */
export type ApiBookingOrigin = {
  incomeId?: number | null
  transferId?: number | null
  salaryId?: number | null
  expenseId?: number | null
}

export type ApiBooking = {
  id: number
  accountId: number
  oppositeAccountId: number
  name: string
  /** `Money`: a fixed two-decimal string. */
  amount: string
  type: BookingTypeValue
  issued: string
  origin: ApiBookingOrigin
  created: string
  updated: string
}

/** `BookingPage`: one page of bookings, with the sum of `amount` over every filtered row (#842). */
export type BookingPage = Page<ApiBooking> & { totals: { amount: string } }

/** The filters of `GET /bookings`. The last four are the table's own; the summary takes none of them. */
export type BookingsQuery = {
  types?: BookingTypeValue[]
  accountIds?: number[]
  accountTypes?: AccountTypeValue[]
  userId?: number
  projectId?: number
  year?: number
  month?: number
  oppositeAccountIds?: number[]
  amountMin?: string
  amountMax?: string
  q?: string
}

/** `BookingsQuery` as the contract's query params (design.md §8.2). */
export const bookingsParams = (q: BookingsQuery) => ({
  type: q.types,
  accountId: q.accountIds,
  accountType: q.accountTypes,
  userId: q.userId,
  projectId: q.projectId,
  year: q.year,
  month: q.month,
  oppositeAccountId: q.oppositeAccountIds,
  amountMin: q.amountMin,
  amountMax: q.amountMax,
  q: q.q,
})

/**
 * The sort keys of `GET /bookings` that the bookings tables offer, a subset of
 * `BookingRepository.sortable` (`backend/app/repositories/booking.py`): a key the backend drops is a
 * 422 for the page.
 */
export const BOOKING_SORTABLE = ['issued', 'created', 'name', 'type', 'amount', 'account_id', 'opposite_account_id'] as const

/** `GET /bookings`'s order when the URL names none (`BookingRepository.default_sort`), as table sort state. */
export const BOOKING_DEFAULT_SORTING = [{ id: 'booking_issued', desc: true }]

/** The contract's `AmountFilter`. */
const decimal = (values: string[] = []): string | undefined => values.find((v) => /^-?\d{1,10}(\.\d{1,2})?$/.test(v))

/**
 * The bookings table's URL filters (`f.booking_*`) as an API query, key by key: a key not listed
 * here never reaches the API, and a value the API would refuse is dropped. The page adds what the
 * server forces (a member's `userId`, a project's `projectId`) on top, so the URL cannot widen it.
 */
export const bookingsQueryOf = (filters: TableFilters): BookingsQuery => {
  const amountMin = decimal(filters.booking_amount_min)
  const amountMax = decimal(filters.booking_amount_max)
  // A range the API refuses (min above max) is no filter rather than an error page.
  const inverted = amountMin !== undefined && amountMax !== undefined && Number(amountMin) > Number(amountMax)
  return {
    accountIds: urlIds(filters.booking_account_id),
    oppositeAccountIds: urlIds(filters.booking_opposite_account_id),
    types: urlEnum(BookingType, filters.booking_type),
    amountMin: inverted ? undefined : amountMin,
    amountMax: inverted ? undefined : amountMax,
  }
}

/** One group of `GET /bookings/summary`; only the requested group keys are set. */
export type BookingSummaryRow = {
  type: BookingTypeValue | null
  month: number | null
  userId: number | null
  accountType: AccountTypeValue | null
  credited: string
  debited: string
  total: string
}

/** One group of the summary as the balance sheets add it up: `total` parsed, both keys set. */
export type Balancesheet = {
  type: BookingTypeValue
  accountType: AccountTypeValue
  total: number
}

/** A booking as the tables render it: dates parsed, both accounts resolved. */
export type BookingRow = Omit<ApiBooking, 'issued' | 'created' | 'updated'> & {
  issued: Date
  created: Date
  updated: Date
  account?: ApiAccount
  oppositeAccount?: ApiAccount
}

export const toBookingRow = (booking: ApiBooking, accounts: Map<number, ApiAccount>): BookingRow => ({
  ...booking,
  issued: new Date(booking.issued),
  created: new Date(booking.created),
  updated: new Date(booking.updated),
  account: accounts.get(booking.accountId),
  oppositeAccount: accounts.get(booking.oppositeAccountId),
})

export const byId = (accounts: ApiAccount[]): Map<number, ApiAccount> => new Map(accounts.map((account) => [account.id, account]))

/**
 * The total of the summary rows matching `where` — what `getTotal(getBookingsSum({ bookingTypes, fromAccountTypes }))`
 * computed with one query per figure; one grouped summary now answers all of a widget's figures.
 */
export const sumOf = (rows: BookingSummaryRow[], where: { accountType?: AccountTypeValue; types?: BookingTypeValue[] } = {}): number =>
  rows
    .filter((row) => where.accountType === undefined || row.accountType === where.accountType)
    .filter((row) => where.types === undefined || (row.type !== null && where.types.includes(row.type)))
    .reduce((sum, row) => sum + parseFloat(row.total), 0)
